export const getAnalyticsFiles = (files = []) => {
  return files.filter((file) => {
    return (
      isParentValue(file.isParent) &&
      file.parent === null
    );
  });
};

export const getAnimatedValueConfig = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return {
      isNumeric: true,
      value,
      prefix: '',
      suffix: '',
      decimals: Number.isInteger(value) ? 0 : 2,
    };
  }

  if (typeof value !== 'string') {
    return { isNumeric: false };
  }

  const match = value.match(/^([^0-9.-]*)(-?\d+(?:\.\d+)?)(.*)$/);

  if (!match) {
    return { isNumeric: false };
  }

  const numericValue = Number(match[2]);

  if (!Number.isFinite(numericValue)) {
    return { isNumeric: false };
  }

  return {
    isNumeric: true,
    value: numericValue,
    prefix: match[1],
    suffix: match[3],
    decimals: match[2].includes('.') ? 2 : 0,
  };
};

export const formatAnimatedValue = ({ value, prefix, suffix, decimals }) => {
  return `${prefix}${value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}${suffix}`;
};

export const formatCodeAmount = (amount) => {
  return amount.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: Number.isInteger(amount) ? 0 : 2
  });
};

export const getLatestRepricedFiles = (files = []) => {
  return files
    .map((file) => {
      return Array.isArray(file.versions) && file.versions.length > 0
        ? file.versions[file.versions.length - 1]
        : null;
    })
    .filter((file) => Array.isArray(file?.data?.file?.claims));
};

const isParentValue = (value) => {
  return value === true || value === 'true' || value === 1;
};

const toNumber = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

const toFiniteNumber = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
};

const getClaims = (file) => {
  const claims = file.data?.file?.claims;
  return Array.isArray(claims) ? claims : [];
};

const getServiceLines = (claim) => {
  return Array.isArray(claim?.serviceLines) ? claim.serviceLines : [];
};

const normalizeHcpcs = (value) => {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  return String(value).replace(/^HC:/, '').split(':')[0].trim();
};

const normalizeServiceLineQuery = (value) => {
  return normalizeHcpcs(value).toLowerCase();
};

const getSubscriberDisplayName = (subscriber = {}, fallback = '') => {
  const firstName = subscriber.firstName || subscriber.firstname;
  const displayName = [firstName, subscriber.name]
    .map((value) => normalizeAddressPart(value))
    .filter(Boolean)
    .join(' ');

  return displayName || fallback;
};

const normalizeAddressPart = (value) => {
  if (value === undefined || value === null) {
    return '';
  }

  return String(value).trim();
};

const getAddressKey = ({ address1, city, state, zip }) => {
  return [address1, city, state, zip]
    .map((value) => normalizeAddressPart(value).toUpperCase())
    .filter(Boolean)
    .join('|');
};

const getProviderGeoCacheKey = (providerId) => {
  const normalizedProviderId = normalizeAddressPart(providerId);

  return normalizedProviderId ? `provider:${normalizedProviderId}` : '';
};

const getAddressGeoCacheKey = ({ address1, city, state, zip }) => {
  const addressKey = getAddressKey({ address1, city, state, zip });

  return addressKey ? `address:${addressKey}` : '';
};

const serviceLineMatchesNormalizedQuery = (serviceLine, normalizedQuery) => {
  const normalizedHcpcs = normalizeHcpcs(serviceLine?.hcpcs).toLowerCase();

  return Boolean(normalizedQuery)
    && normalizedHcpcs === normalizedQuery;
};

export const STATE_ABBREVIATIONS = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  DC: 'District of Columbia'
};

export const STATE_INITIALS_BY_NAME = Object.entries(STATE_ABBREVIATIONS)
  .reduce((initialsByName, [initials, name]) => ({
    ...initialsByName,
    [name]: initials,
    [name.toUpperCase()]: initials
  }), {});

export const getStateName = (state = '') => {
  const normalizedState = String(state).trim();
  const stateInitials = STATE_INITIALS_BY_NAME[normalizedState.toUpperCase()];

  return STATE_ABBREVIATIONS[normalizedState.toUpperCase()]
    || STATE_ABBREVIATIONS[stateInitials]
    || normalizedState;
};

export const getStateInitials = (stateName = '') => {
  const normalizedStateName = String(stateName).trim();

  return STATE_INITIALS_BY_NAME[normalizedStateName]
    || STATE_INITIALS_BY_NAME[normalizedStateName.toUpperCase()]
    || '';
};

export const hashString = (value = '') => {
  return String(value).split('').reduce((hash, character) => {
    return ((hash << 5) - hash) + character.charCodeAt(0);
  }, 0);
};

export const getFallbackOffset = (point) => {
  const hash = hashString(`${point.address1}|${point.city}|${point.state}|${point.zip}`);
  const angle = (Math.abs(hash) % 360) * (Math.PI / 180);
  const distance = 8 + (Math.abs(hash >> 4) % 18);

  return {
    x: Math.cos(angle) * distance,
    y: Math.sin(angle) * distance
  };
};

export const getPointRadius = (point, baseSize) => {
  return Math.max(baseSize, Math.min(28, baseSize + Math.sqrt(point.count || 1) * 2));
};

export const getClusterRadius = (cluster, baseSize) => {
  return Math.max(baseSize + 4, Math.min(34, baseSize + Math.sqrt(cluster.count || 1) * 3));
};

export const getDistance = (firstPoint, secondPoint) => {
  return Math.hypot(firstPoint.x - secondPoint.x, firstPoint.y - secondPoint.y);
};

export const getPointCount = (point) => {
  return point.count || 1;
};

export const createPointClusters = (points = [], zoomScale = 1) => {
  const clusterDistance = 34 / zoomScale;
  const clusters = [];

  points.forEach((point) => {
    const matchingCluster = clusters.find((cluster) => {
      return getDistance(cluster, point) <= clusterDistance;
    });

    if (!matchingCluster) {
      clusters.push({
        id: point.id,
        x: point.x,
        y: point.y,
        count: getPointCount(point),
        members: [point]
      });
      return;
    }

    const pointCount = getPointCount(point);
    const nextCount = matchingCluster.count + pointCount;

    matchingCluster.x = ((matchingCluster.x * matchingCluster.count) + (point.x * pointCount)) / nextCount;
    matchingCluster.y = ((matchingCluster.y * matchingCluster.count) + (point.y * pointCount)) / nextCount;
    matchingCluster.count = nextCount;
    matchingCluster.members.push(point);
  });

  return clusters.map((cluster) => {
    if (cluster.members.length === 1) {
      return {
        ...cluster.members[0],
        clusterCount: cluster.count,
        isCluster: false
      };
    }

    return {
      ...cluster,
      id: `cluster-${cluster.members[0].id}-${Math.round(cluster.x)}-${Math.round(cluster.y)}-${cluster.members.length}`,
      clusterCount: cluster.count,
      isCluster: true
    };
  });
};

const isUsableStreetAddress = (address1) => {
  const normalizedAddress = normalizeAddressPart(address1).toUpperCase();

  return Boolean(normalizedAddress)
    && !['UNKNOWN', 'N/A', 'NA', 'NONE', 'NULL', 'NOT AVAILABLE'].includes(normalizedAddress)
    && !normalizedAddress.includes('UNKNOWN');
};

const hasGeocodableAddress = (point) => {
  return isUsableStreetAddress(point.address1)
    || Boolean(normalizeAddressPart(point.city))
    || Boolean(normalizeAddressPart(point.zip));
};

const getGeocodeBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const savedBaseUrl = window.localStorage.getItem('veritas:nominatimBaseUrl');

    if (savedBaseUrl) {
      return savedBaseUrl.replace(/\/$/, '');
    }
  }

  return (process.env.REACT_APP_NOMINATIM_URL || 'https://nominatim.openstreetmap.org')
    .replace(/\/$/, '');
};

const GEOCODE_RATE_LIMIT_COOLDOWN_MS = 10 * 60 * 1000;
const GEOCODE_RATE_LIMIT_CACHE_KEY = 'veritas:geocode:nominatim:rateLimitUntil';
const pendingGeocodeRequests = new Map();

const getGeocodeCacheKey = (point, useStreetAddress) => {
  return `veritas:geocode:nominatim:v1:${getAddressKey({
    address1: useStreetAddress ? point.address1 : '',
    city: point.city,
    state: point.state,
    zip: point.zip
  })}`;
};

const getGeocodeRateLimitUntil = () => {
  if (typeof window === 'undefined') {
    return 0;
  }

  const storedValue = Number(window.localStorage.getItem(GEOCODE_RATE_LIMIT_CACHE_KEY));

  return Number.isFinite(storedValue) ? storedValue : 0;
};

const isGeocodeRateLimited = () => {
  return Date.now() < getGeocodeRateLimitUntil();
};

const setGeocodeRateLimitCooldown = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(
    GEOCODE_RATE_LIMIT_CACHE_KEY,
    String(Date.now() + GEOCODE_RATE_LIMIT_COOLDOWN_MS)
  );
};

const isRateLimitError = (error) => {
  return error?.name === 'GeocodeRateLimitError';
};

const getCachedGeocodeResult = (cacheKey) => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const cachedValue = window.localStorage.getItem(cacheKey);

  if (cachedValue === null) {
    return undefined;
  }

  try {
    return JSON.parse(cachedValue);
  } catch (error) {
    window.localStorage.removeItem(cacheKey);
    return undefined;
  }
};

const cacheGeocodeResult = (cacheKey, value) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(cacheKey, JSON.stringify(value));
};

const wait = (milliseconds) => {
  return new Promise((resolve) => {
    setTimeout(resolve, milliseconds);
  });
};

const geocodeBillingProviderPoint = async (point, useStreetAddress) => {
  if (isGeocodeRateLimited()) {
    return null;
  }

  const params = new URLSearchParams({
    format: 'jsonv2',
    limit: '1',
    addressdetails: '0',
    country: 'United States'
  });

  if (useStreetAddress) {
    params.set('street', point.address1);
  }

  if (point.city) {
    params.set('city', point.city);
  }

  if (point.state) {
    params.set('state', point.state);
  }

  if (point.zip) {
    params.set('postalcode', point.zip);
  }

  const requestUrl = `${getGeocodeBaseUrl()}/search?${params.toString()}`;
  const pendingRequest = pendingGeocodeRequests.get(requestUrl);

  if (pendingRequest) {
    return pendingRequest;
  }

  const request = fetch(requestUrl)
    .then(async (response) => {
      if (response.status === 429) {
        setGeocodeRateLimitCooldown();
        const error = new Error('Nominatim geocoding rate limit reached');
        error.name = 'GeocodeRateLimitError';
        throw error;
      }

      if (!response.ok) {
        return null;
      }

      const results = await response.json();
      const firstResult = Array.isArray(results) ? results[0] : null;
      const lat = toFiniteNumber(firstResult?.lat);
      const lng = toFiniteNumber(firstResult?.lon);

      if (lat === null || lng === null) {
        return null;
      }

      return {
        lat,
        lng,
        geocodedBy: useStreetAddress ? 'street' : 'city'
      };
    })
    .catch((error) => {
      setGeocodeRateLimitCooldown();
      const rateLimitError = new Error(error?.message || 'Geocoding request failed');
      rateLimitError.name = 'GeocodeRateLimitError';
      throw rateLimitError;
    })
    .finally(() => {
      pendingGeocodeRequests.delete(requestUrl);
    });

  pendingGeocodeRequests.set(requestUrl, request);

  return request;
};

export const geocodeBillingProviderMapPoints = async (points = []) => {
  const geocodedPoints = [];

  for (let index = 0; index < points.length; index += 1) {
    const point = points[index];
    const hasExistingCoordinates = Number.isFinite(point.lat) && Number.isFinite(point.lng);

    if (hasExistingCoordinates) {
      geocodedPoints.push(point);
      continue;
    }

    if (!hasGeocodableAddress(point)) {
      geocodedPoints.push(point);
      continue;
    }

    const useStreetAddress = isUsableStreetAddress(point.address1);
    const cacheKey = getGeocodeCacheKey(point, useStreetAddress);
    const cachedResult = getCachedGeocodeResult(cacheKey);

    if (cachedResult !== undefined) {
      geocodedPoints.push(cachedResult ? { ...point, ...cachedResult } : point);
      continue;
    }

    if (isGeocodeRateLimited()) {
      geocodedPoints.push(...points.slice(index));
      break;
    }

    let result = null;

    try {
      result = await geocodeBillingProviderPoint(point, useStreetAddress);

      if (!result && useStreetAddress) {
        const cityCacheKey = getGeocodeCacheKey(point, false);
        const cachedCityResult = getCachedGeocodeResult(cityCacheKey);

        if (cachedCityResult !== undefined) {
          result = cachedCityResult;
        } else {
          await wait(1100);
          result = await geocodeBillingProviderPoint(point, false);
          cacheGeocodeResult(cityCacheKey, result);
        }
      }
    } catch (error) {
      if (isRateLimitError(error)) {
        geocodedPoints.push(point, ...points.slice(index + 1));
        break;
      }

      result = null;
    }

    cacheGeocodeResult(cacheKey, result);
    geocodedPoints.push(result ? { ...point, ...result } : point);
    await wait(1100);
  }

  return geocodedPoints;
};

const getMedian = (values = []) => {
  if (values.length === 0) {
    return 0;
  }

  const sortedValues = [...values].sort((a, b) => a - b);
  const middleIndex = Math.floor(sortedValues.length / 2);

  if (sortedValues.length % 2 === 0) {
    return (sortedValues[middleIndex - 1] + sortedValues[middleIndex]) / 2;
  }

  return sortedValues[middleIndex];
};

export const getTotalClaims = (files = []) => {
  return getAnalyticsFiles(files)
    .reduce((total, file) => {
      return total + getClaims(file).length;
    }, 0);
};

export const getTotalBilled = (files = []) => {
  return getAnalyticsFiles(files)
    .reduce((total, file) => {
      const fileTotal = getClaims(file).reduce((claimTotal, claim) => {
        return claimTotal + toNumber(claim.claimInfo?.totalAmount);
      }, 0);

      return total + fileTotal;
    }, 0);
};

export const getTotalSavings = (files = []) => {
  let total = 0;

  files.forEach((file) => {
    getClaims(file).forEach((claim) => {
      total += toNumber(claim.pricing?.savingsAmount);
    });
  });

  return total;
};

export const getServiceLineMapSummary = (files = [], serviceLineQuery = '', repricedFiles = []) => {
  const normalizedQuery = normalizeServiceLineQuery(serviceLineQuery);
  let totalClaims = 0;
  let totalBilled = 0;
  let totalSavings = 0;

  if (!normalizedQuery) {
    return null;
  }

  getAnalyticsFiles(files).forEach((file) => {
    getClaims(file).forEach((claim) => {
      let hasMatchingServiceLine = false;
      let claimBilled = 0;

      getServiceLines(claim).forEach((serviceLine) => {
        if (!serviceLineMatchesNormalizedQuery(serviceLine, normalizedQuery)) {
          return;
        }

        hasMatchingServiceLine = true;
        claimBilled += toNumber(serviceLine?.chargeAmount);
      });

      if (!hasMatchingServiceLine) {
        return;
      }

      totalClaims += 1;
      totalBilled += claimBilled;
    });
  });

  repricedFiles.forEach((file) => {
    getClaims(file).forEach((claim) => {
      getServiceLines(claim).forEach((serviceLine) => {
        if (!serviceLineMatchesNormalizedQuery(serviceLine, normalizedQuery)) {
          return;
        }

        totalSavings += toNumber(serviceLine?.pricing?.savingsAmount);
      });
    });
  });

  return [
    {
      id: 'totalClaims',
      title: 'Total Claims',
      body: totalClaims
    },
    {
      id: 'totalBilled',
      title: 'Total Billed',
      body: formatCodeAmount(totalBilled)
    },
    {
      id: 'totalSavings',
      title: 'Total Saved',
      body: formatCodeAmount(totalSavings)
    }
  ];
};

export const getTopBilledCodes = (files = []) => {
  const codesByHcpcs = new Map();

  getAnalyticsFiles(files).forEach((file) => {
    getClaims(file).forEach((claim) => {
      getServiceLines(claim).forEach((serviceLine) => {
        const hcpcs = normalizeHcpcs(serviceLine?.hcpcs);
        const chargeAmount = toFiniteNumber(serviceLine?.chargeAmount);

        if (!hcpcs || chargeAmount === null) {
          return;
        }

        const existingCode = codesByHcpcs.get(hcpcs) || {
          hcpcs,
          count: 0,
          amounts: []
        };

        existingCode.count += 1;
        existingCode.amounts.push(chargeAmount);
        codesByHcpcs.set(hcpcs, existingCode);
      });
    });
  });

  return Array.from(codesByHcpcs.values())
    .map((code) => ({
      hcpcs: code.hcpcs,
      count: code.count,
      min: Math.min(...code.amounts),
      median: getMedian(code.amounts),
      max: Math.max(...code.amounts)
    }))
    .sort((firstCode, secondCode) => {
      if (secondCode.count !== firstCode.count) {
        return secondCode.count - firstCode.count;
      }

      return secondCode.max - firstCode.max;
    })
    .slice(0, 5);
};

export const getTopBilledMembers = (files = []) => {
  const membersBySubscriberId = new Map();

  getAnalyticsFiles(files).forEach((file) => {
    getClaims(file).forEach((claim) => {
      const subscriber = claim?.loops?.subscriber;
      const subscriberId = normalizeAddressPart(subscriber?.id);
      const totalAmount = toFiniteNumber(claim?.claimInfo?.totalAmount);

      if (!subscriberId || totalAmount === null) {
        return;
      }

      const existingMember = membersBySubscriberId.get(subscriberId) || {
        subscriberId,
        name: getSubscriberDisplayName(subscriber, subscriberId),
        totalAmount: 0,
        count: 0
      };
      const subscriberName = getSubscriberDisplayName(subscriber, existingMember.name);

      existingMember.name = subscriberName;
      existingMember.totalAmount += totalAmount;
      existingMember.count += 1;
      membersBySubscriberId.set(subscriberId, existingMember);
    });
  });

  return Array.from(membersBySubscriberId.values())
    .sort((firstMember, secondMember) => {
      if (secondMember.totalAmount !== firstMember.totalAmount) {
        return secondMember.totalAmount - firstMember.totalAmount;
      }

      return secondMember.count - firstMember.count;
    })
    .slice(0, 5);
};

export const getTopBilledStates = (files = []) => {
  const statesByInitials = new Map();

  getAnalyticsFiles(files).forEach((file) => {
    getClaims(file).forEach((claim) => {
      const rawState = claim?.loops?.billingProvider?.address?.state;
      const stateName = getStateName(rawState);
      const stateInitials = getStateInitials(stateName) || normalizeAddressPart(rawState).toUpperCase();
      const totalAmount = toFiniteNumber(claim?.claimInfo?.totalAmount);

      if (!stateInitials || totalAmount === null) {
        return;
      }

      const existingState = statesByInitials.get(stateInitials) || {
        stateInitials,
        name: stateName,
        totalAmount: 0,
        count: 0
      };

      existingState.totalAmount += totalAmount;
      existingState.count += 1;
      statesByInitials.set(stateInitials, existingState);
    });
  });

  return Array.from(statesByInitials.values())
    .sort((firstState, secondState) => {
      if (secondState.totalAmount !== firstState.totalAmount) {
        return secondState.totalAmount - firstState.totalAmount;
      }

      return secondState.count - firstState.count;
    })
    .slice(0, 5);
};

export const getTopBilledGroups = (files = []) => {
  const groupsByReferenceId = new Map();

  getAnalyticsFiles(files).forEach((file) => {
    getClaims(file).forEach((claim) => {
      const otherInsurances = Array.isArray(claim?.otherInsurances)
        ? claim.otherInsurances
        : [claim?.otherInsurances].filter(Boolean);
      const totalAmount = toFiniteNumber(claim?.claimInfo?.totalAmount);

      if (totalAmount === null) {
        return;
      }

      otherInsurances.forEach((otherInsurance) => {
        const referenceId = normalizeAddressPart(
          otherInsurance?.refereceId || otherInsurance?.referenceId
        );

        if (!referenceId) {
          return;
        }

        const existingGroup = groupsByReferenceId.get(referenceId) || {
          referenceId,
          totalAmount: 0,
          count: 0
        };

        existingGroup.totalAmount += totalAmount;
        existingGroup.count += 1;
        groupsByReferenceId.set(referenceId, existingGroup);
      });
    });
  });

  return Array.from(groupsByReferenceId.values())
    .sort((firstGroup, secondGroup) => {
      if (secondGroup.totalAmount !== firstGroup.totalAmount) {
        return secondGroup.totalAmount - firstGroup.totalAmount;
      }

      return secondGroup.count - firstGroup.count;
    })
    .slice(0, 5);
};

export const createBillingProviderGeoCache = (points = []) => {
  const geoCache = new Map();

  const cachePoint = (cacheKey, point) => {
    if (!cacheKey || !Number.isFinite(point.lat) || !Number.isFinite(point.lng)) {
      return;
    }

    geoCache.set(cacheKey, {
      lat: point.lat,
      lng: point.lng
    });
  };

  points.forEach((point) => {
    cachePoint(getProviderGeoCacheKey(point.providerId), point);
    (point.providerIds || []).forEach((providerId) => {
      cachePoint(getProviderGeoCacheKey(providerId), point);
    });
    cachePoint(getAddressGeoCacheKey(point), point);
  });

  return geoCache;
};

const getCachedProviderGeo = ({ providerId, address1, city, state, zip }, geoCache) => {
  if (!geoCache) {
    return null;
  }

  return geoCache.get(getProviderGeoCacheKey(providerId))
    || geoCache.get(getAddressGeoCacheKey({ address1, city, state, zip }))
    || null;
};

export const getBillingProviderMapPoints = (files = [], options = {}) => {
  const pointsByAddress = new Map();
  const serviceLineQuery = normalizeServiceLineQuery(options.serviceLineQuery || '');
  const isServiceLineSearch = Boolean(serviceLineQuery);
  const geoCache = options.geoCache || null;

  getAnalyticsFiles(files).forEach((file) => {
    getClaims(file).forEach((claim, claimIndex) => {
      let hasMatchingServiceLine = !isServiceLineSearch;
      let billedAmount = 0;

      getServiceLines(claim).forEach((serviceLine) => {
        if (isServiceLineSearch && !serviceLineMatchesNormalizedQuery(serviceLine, serviceLineQuery)) {
          return;
        }

        hasMatchingServiceLine = true;
        billedAmount += toNumber(serviceLine?.chargeAmount);
      });

      if (!hasMatchingServiceLine) {
        return;
      }

      const billingProvider = claim?.loops?.billingProvider;
      const address = billingProvider?.address;
      const provider = billingProvider?.provider;

      if (!address) {
        return;
      }

      const address1 = normalizeAddressPart(address.address1);
      const city = normalizeAddressPart(address.city);
      const state = normalizeAddressPart(address.state);
      const zip = normalizeAddressPart(address.zip || address.zipCode || address.postalCode);

      if (!address1 && !city && !state && !zip) {
        return;
      }

      const addressKey = getAddressKey({ address1, city, state, zip })
        || `${file.id || file.slug || 'file'}-${claimIndex}`;
      const providerId = normalizeAddressPart(provider?.id);
      const providerName = normalizeAddressPart(provider?.name);
      const pointKey = isServiceLineSearch && providerId
        ? providerId
        : addressKey;
      const existingPoint = pointsByAddress.get(pointKey);
      const lat = toFiniteNumber(billingProvider?.geo?.lat);
      const lng = toFiniteNumber(billingProvider?.geo?.lng ?? billingProvider?.geo?.lon);
      const cachedGeo = getCachedProviderGeo({
        providerId,
        address1,
        city,
        state,
        zip
      }, geoCache);
      const resolvedLat = lat ?? cachedGeo?.lat ?? null;
      const resolvedLng = lng ?? cachedGeo?.lng ?? null;

      if (existingPoint) {
        existingPoint.count += 1;
        existingPoint.totalBilled += billedAmount;
        existingPoint.claimIds.push(claim?.claimInfo?.claimId || null);
        if (!Number.isFinite(existingPoint.lat) && Number.isFinite(resolvedLat)) {
          existingPoint.lat = resolvedLat;
        }
        if (!Number.isFinite(existingPoint.lng) && Number.isFinite(resolvedLng)) {
          existingPoint.lng = resolvedLng;
        }
        if (providerId && !existingPoint.providerIds.includes(providerId)) {
          existingPoint.providerIds.push(providerId);
        }
        if (providerName && !existingPoint.providerNames.includes(providerName)) {
          existingPoint.providerNames.push(providerName);
        }
        return;
      }

      pointsByAddress.set(pointKey, {
        id: pointKey,
        address1,
        city,
        state,
        zip,
        lat: resolvedLat,
        lng: resolvedLng,
        count: 1,
        totalBilled: billedAmount,
        claimIds: [claim?.claimInfo?.claimId || null],
        providerId,
        providerIds: providerId ? [providerId] : [],
        providerNames: providerName ? [providerName] : [],
        name: providerName,
        type: 'provider'
      });
    });
  });

  return Array.from(pointsByAddress.values());
};

export const getAnalyticsOverview = ({
  files = [],
  repricedFiles = [],
  icons = {},
  renderTopBilledCodesBody,
  renderTopBilledMembersBody,
  renderTopBilledStatesBody,
  renderTopBilledGroupsBody
} = {}) => {
  const totalClaims = getTotalClaims(files);
  const totalBilled = getTotalBilled(files);
  const totalSavings = getTotalSavings(repricedFiles);
  const topBilledCodes = getTopBilledCodes(files);
  const topBilledMembers = getTopBilledMembers(files);
  const topBilledStates = getTopBilledStates(files);
  const topBilledGroups = getTopBilledGroups(files);

  return {
    overviewCards: [
      {
        id: 'totalClaims',
        title: 'Total Claims',
        body: totalClaims,
        icon: icons.totalClaims
      },
      {
        id: 'totalBilled',
        title: 'Total Billed',
        body: `$${totalBilled.toFixed(2)}`,
        icon: icons.totalBilled
      },
      {
        id: 'totalSavings',
        title: 'Total Savings',
        body: `$${totalSavings.toFixed(2)}`,
        icon: icons.totalSavings
      },
      {
        id: 'topBilledCodes',
        title: 'Top 5 Billed Codes',
        body: renderTopBilledCodesBody
          ? renderTopBilledCodesBody(topBilledCodes)
          : topBilledCodes,
        icon: icons.topBilledCodes
      },
      {
        id: 'topBilledMembers',
        title: 'Top 5 Billed Members',
        body: renderTopBilledMembersBody
          ? renderTopBilledMembersBody(topBilledMembers)
          : topBilledMembers,
        icon: icons.topBilledMembers
      },
      {
        id: 'topBilledStates',
        title: 'Top 5 Billed States',
        body: renderTopBilledStatesBody
          ? renderTopBilledStatesBody(topBilledStates)
          : topBilledStates,
        icon: icons.topBilledStates
      },
      {
        id: 'topBilledGroups',
        title: 'Top 5 Billed Groups',
        body: renderTopBilledGroupsBody
          ? renderTopBilledGroupsBody(topBilledGroups)
          : topBilledGroups,
        icon: icons.topBilledGroups
      }
    ]
  };
};
